import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import AppLogo from '@/components/AppLogo';

const alwaysLinks = [
  { label: 'Home', path: '/' },
  { label: 'Pricing', path: '/pricing' }
];

const resourceLinks = [
  {
    label: 'Resources',
    path: '/resources'
  }
];

const legalLinks = [
  { label: 'Privacy Policy', path: '/privacy' },
  { label: 'Terms of Service', path: '/terms' }
];

export default function MainMenu({ tone = 'light', className = '' }) {
  const [open, setOpen] = useState(false);
  const closeButtonRef = useRef(null);
  const triggerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout, user } = useAuth();

  const closeMenu = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeMenu, open]);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate('/');
  };

  const buttonTone =
    tone === 'dark'
      ? 'border-white/20 bg-white/10 text-white hover:bg-white/20'
      : 'border-border bg-white text-foreground shadow-soft hover:bg-surface';

  const linkClass = (path) => {
    const isActive =
      path === '/resources'
        ? location.pathname === '/resources' || location.pathname.startsWith('/resources/')
        : location.pathname === path;

    return `block rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
      isActive
        ? 'bg-primary/10 text-primary'
        : 'text-muted-foreground hover:bg-surface hover:text-foreground'
    }`;
  };

  const drawer = (
    <div
      className={`fixed inset-0 z-[100] h-dvh ${open ? 'pointer-events-auto' : 'pointer-events-none'}`}
      aria-hidden={open ? undefined : 'true'}
      {...(!open ? { inert: '' } : {})}
    >
      <button
        type="button"
        aria-label="Close navigation menu"
        onClick={closeMenu}
        className={`absolute inset-0 h-full w-full bg-slate-950/70 transition-opacity duration-200 ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <aside
        id="main-navigation-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Main navigation"
        className={`absolute inset-y-0 left-0 flex h-dvh max-h-dvh min-h-dvh w-[min(20rem,85vw)] flex-col bg-white shadow-2xl transition-transform duration-200 ease-out ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex shrink-0 items-start justify-between border-b border-border px-5 py-5">
          <Link to="/" onClick={closeMenu} className="inline-flex">
            <AppLogo variant="drawer" />
          </Link>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="Close navigation menu"
            onClick={closeMenu}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-surface hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-5" aria-label="Main navigation links">
          <div className="space-y-2">
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" onClick={closeMenu} className={linkClass('/dashboard')}>
                  Dashboard
                </Link>
                {user?.is_admin && (
                  <Link to="/admin" onClick={closeMenu} className={linkClass('/admin')}>
                    Admin
                  </Link>
                )}
                <Link to="/account" onClick={closeMenu} className={linkClass('/account')}>
                  Account
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="block w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-muted-foreground transition-colors hover:bg-surface hover:text-foreground"
                >
                  Log Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/register"
                  onClick={closeMenu}
                  className="block rounded-lg bg-primary px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-hover"
                >
                  Sign Up
                </Link>
                <Link
                  to="/login"
                  onClick={closeMenu}
                  className="block rounded-lg border border-border px-3 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-surface"
                >
                  Log In
                </Link>
              </>
            )}
          </div>

          <div className="mt-5 border-t border-border pt-5">
            <div className="space-y-1">
              {alwaysLinks.map((item) => (
                <Link key={item.path} to={item.path} onClick={closeMenu} className={linkClass(item.path)}>
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-5 border-t border-border pt-5">
            <p className="px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Resources
            </p>
            <div className="mt-2 space-y-1">
              {resourceLinks.map((item) => (
                <Link key={item.path} to={item.path} onClick={closeMenu} className={linkClass(item.path)}>
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-auto border-t border-border pt-5">
            <div className="space-y-1">
              {legalLinks.map((item) => (
                <Link key={item.path} to={item.path} onClick={closeMenu} className={linkClass(item.path)}>
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </nav>
      </aside>
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Open navigation menu"
        aria-expanded={open}
        aria-controls="main-navigation-drawer"
        onClick={() => setOpen(true)}
        className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition-colors ${buttonTone} ${className}`}
        data-testid="hamburger-menu-btn"
      >
        <span className="sr-only">Open navigation menu</span>
        <span aria-hidden="true" className="flex h-5 w-5 flex-col justify-between">
          <span className="block h-0.5 w-5 rounded-full bg-current" />
          <span className="block h-0.5 w-5 rounded-full bg-current" />
          <span className="block h-0.5 w-5 rounded-full bg-current" />
        </span>
      </button>

      {typeof document !== 'undefined' ? createPortal(drawer, document.body) : null}
    </>
  );
}
